import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Briefcase, Megaphone, ShoppingBag, Ticket, DollarSign,
  TrendingUp, Receipt, FileText, AlertCircle, CheckCircle2, PieChart, Calendar,
  ArrowUpRight, ArrowDownRight, Minus, Paperclip, Camera, Image as ImageIcon, Upload,
} from 'lucide-react';
import { FinanceLayout, Kpi, money, Pill } from './FinanceLayout';
import { useAuth } from '../../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [s, b] = await Promise.all([
          axios.get(`${API}/admin/accounting/summary?months=12`, { headers }),
          axios.get(`${API}/admin/finance/revenue-breakdown`, { headers }),
        ]);
        setSummary(s.data);
        setBreakdown(b.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading || !summary || !breakdown) {
    return (
      <FinanceLayout title="Dashboard">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
        </div>
      </FinanceLayout>
    );
  }

  const tm = summary.this_month || {};
  const totals = summary.totals;
  const fy = summary.fiscal_year || { year: new Date().getUTCFullYear(), label: '', revenue: 0, fees: 0, expenses: 0, net: 0 };
  const receiptStats = summary.receipts || { expense_count: 0, missing_count: 0, with_count: 0 };
  const subs = summary.subscribers;
  const activeSubs = (summary.subs || []).filter(s => s.status === 'active' && !s.cancel_at_period_end);

  return (
    <FinanceLayout title="Dashboard" subtitle="Executive overview">
      {/* HERO RECEIPT UPLOAD — first thing the founder sees on every visit */}
      <ReceiptUploadHero navigate={navigate} />

      {/* Hero MRR + net income */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={TrendingUp} label="This month revenue" value={money(tm.revenue_total)}
             sub={`${money(tm.subscription_revenue)} subs · ${money(tm.tour_revenue)} tours`}
             accent="from-teal-500 to-blue-600" testid="kpi-revenue-month" />
        <Kpi icon={DollarSign} label="This month net" value={money(tm.net)}
             sub={`after ${money(tm.fees)} fees + ${money(tm.expenses)} expenses`}
             accent="from-emerald-500 to-teal-500" testid="kpi-net-month" />
        <Kpi
          icon={Calendar}
          label={`Net (FY ${fy.year})`}
          value={money(fy.net)}
          sub={`${money(fy.revenue)} rev · Jan–Dec ${fy.year}`}
          accent="from-violet-500 to-fuchsia-600"
          testid="kpi-net-fy"
          bodyContent={<><YoyBadge fy={fy} /><MissingReceiptsLink stats={receiptStats} navigate={navigate} /></>}
        />
        <Kpi icon={PieChart} label="Net (last 12 months)" value={money(totals.net)}
             sub={`${money(totals.revenue)} revenue − ${money((totals.fees || 0) + (totals.expenses || 0))} costs`}
             accent="from-orange-500 to-rose-500" testid="kpi-net-12mo" />
        <Kpi icon={TrendingUp} label="Recurring revenue" value={money(breakdown.subscription_mrr)}
             sub={`${money(breakdown.subscription_arr)} annualized`}
             accent="from-blue-500 to-indigo-600" testid="kpi-mrr" />
        <Kpi icon={FileText} label="Outstanding invoices" value={money(breakdown.outstanding_invoices_total_usd)}
             sub={`${breakdown.outstanding_invoices_count} unpaid`}
             accent="from-amber-500 to-orange-500" testid="kpi-outstanding" />
      </div>

      {/* Revenue by source */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-slate-900">Revenue by source — this month</h2>
          <Pill tone="slate">{tm.label || 'Current'}</Pill>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <SourceRow icon={Briefcase} label="Analytics subscriptions" value={tm.subscription_revenue} total={tm.revenue_total} color="bg-blue-500" />
          <SourceRow icon={Megaphone} label="Advertisers" value={0} total={tm.revenue_total || 1} color="bg-violet-500" hint="No ad revenue recorded this month" />
          <SourceRow icon={Ticket} label="Tour sales" value={tm.tour_revenue} total={tm.revenue_total} color="bg-teal-500" />
          <SourceRow icon={ShoppingBag} label="Merchandise" value={0} total={tm.revenue_total || 1} color="bg-orange-500" hint={breakdown.merch_paid_order_count === 0 ? 'No merch orders yet' : 'Ready to launch'} />
        </div>
      </section>

      {/* Subscribers + active campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-black text-slate-900 mb-3 flex items-center gap-2"><Briefcase className="w-5 h-5" /> Analytics subscribers</h3>
          <div className="flex gap-2 mb-4 flex-wrap">
            <Pill tone="emerald">{subs.active_count} active</Pill>
            {subs.canceling_count > 0 && <Pill tone="amber">{subs.canceling_count} canceling</Pill>}
            {subs.churned_count > 0 && <Pill tone="slate">{subs.churned_count} churned</Pill>}
          </div>
          {activeSubs.length === 0 ? (
            <p className="text-sm text-slate-500">No active subscribers yet.</p>
          ) : (
            <ul className="space-y-2">
              {activeSubs.slice(0, 5).map(s => (
                <li key={s.subscription_id} className="text-sm flex items-baseline justify-between gap-2 py-2 border-t border-slate-100 first:border-t-0 first:pt-0">
                  <div>
                    <div className="font-bold text-slate-900">{s.org_name}</div>
                    <div className="text-xs text-slate-500">{s.contact_name} · {s.contact_email}</div>
                  </div>
                  <div className="font-bold text-slate-900 whitespace-nowrap">{money(s.price_usd)}<span className="text-xs text-slate-500 font-normal">/{s.billing_interval === 'yearly' ? 'yr' : 'mo'}</span></div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-black text-slate-900 mb-3 flex items-center gap-2"><Megaphone className="w-5 h-5" /> Advertising pipeline</h3>
          {breakdown.ad_active_campaigns === 0 ? (
            <div className="text-sm text-slate-600">
              <p>No active campaigns yet.</p>
              <p className="text-slate-500 mt-2">Add your first advertiser in the <strong>Advertisers</strong> tab to start tracking ad revenue.</p>
            </div>
          ) : (
            <div>
              <div className="text-3xl font-black text-slate-900">{money(breakdown.ad_active_value_usd)}</div>
              <p className="text-sm text-slate-600 mt-1">{breakdown.ad_active_campaigns} live campaign{breakdown.ad_active_campaigns === 1 ? '' : 's'}</p>
            </div>
          )}
        </div>
      </div>

      {/* 12-month table */}
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 pb-3 flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-900">Last 12 months — profit & loss</h2>
          <div className="text-sm text-slate-600">
            {money(totals.revenue)} revenue · {money(totals.net)} net
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 text-left font-bold">Month</th>
                <th className="px-4 py-3 text-right font-bold">Subs</th>
                <th className="px-4 py-3 text-right font-bold">Tours</th>
                <th className="px-4 py-3 text-right font-bold">Revenue</th>
                <th className="px-4 py-3 text-right font-bold">Fees</th>
                <th className="px-4 py-3 text-right font-bold">Expenses</th>
                <th className="px-4 py-3 text-right font-bold">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summary.series.slice().reverse().map(m => (
                <tr key={m.month} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5">{m.label}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">{money(m.subscription_revenue)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">{money(m.tour_revenue)}</td>
                  <td className="px-4 py-2.5 text-right font-bold">{money(m.revenue_total)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-500">-{money(m.fees)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-500">-{money(m.expenses)}</td>
                  <td className={`px-4 py-2.5 text-right font-bold ${m.net > 0 ? 'text-emerald-700' : m.net < 0 ? 'text-rose-700' : 'text-slate-900'}`}>
                    {money(m.net)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-teal-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-700">
            <p className="font-bold text-teal-900">Everything is auto-saved for tax time.</p>
            <p className="mt-1">Every transaction, subscription, campaign, and expense is persisted in MongoDB the moment it happens. Use <strong>Settings</strong> → Export CSV to get a yearly tax-ready file.</p>
          </div>
        </div>
      </div>
    </FinanceLayout>
  );
}

const SourceRow = ({ icon: Icon, label, value, total, color, hint }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-slate-600 font-bold mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-xl font-black text-slate-900">{money(value)}</div>
      <div className="h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}></div>
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
};

const ReceiptUploadHero = ({ navigate }) => {
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        sessionStorage.setItem('audiostride_inbox_pending_image', reader.result);
        sessionStorage.setItem('audiostride_inbox_pending_filename', file.name || `receipt-${Date.now()}.jpg`);
      } catch (_) { /* sessionStorage unavailable — Inbox will just show empty state */ }
      navigate('/admin/finance/inbox?source=hero');
    };
    reader.readAsDataURL(file);
  };

  return (
    <section
      className="relative bg-gradient-to-br from-teal-500 via-blue-600 to-indigo-700 rounded-3xl p-5 sm:p-6 shadow-xl text-white overflow-hidden"
      data-testid="receipt-upload-hero"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
      <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-fuchsia-400/20 rounded-full blur-3xl" />
      <div className="relative">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <Receipt className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h2 className="font-black text-xl sm:text-2xl leading-tight">Add a receipt</h2>
            <p className="text-white/90 text-sm mt-0.5">Snap it · we'll auto-parse vendor, date, amount &amp; category</p>
          </div>
        </div>

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          data-testid="hero-camera-input"
        />
        <input
          ref={libraryRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          data-testid="hero-library-input"
        />

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            data-testid="hero-take-photo"
            className="flex flex-col items-center justify-center gap-2 py-6 sm:py-7 rounded-2xl bg-white text-slate-900 font-black shadow-lg active:scale-95 hover:bg-slate-50 transition"
          >
            <Camera className="w-7 h-7 sm:w-8 sm:h-8 text-teal-600" />
            <span className="text-sm sm:text-base">Take photo</span>
          </button>
          <button
            type="button"
            onClick={() => libraryRef.current?.click()}
            data-testid="hero-from-library"
            className="flex flex-col items-center justify-center gap-2 py-6 sm:py-7 rounded-2xl bg-slate-900 text-white font-black shadow-lg active:scale-95 hover:bg-black transition"
          >
            <ImageIcon className="w-7 h-7 sm:w-8 sm:h-8" />
            <span className="text-sm sm:text-base">From library</span>
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigate('/admin/finance/inbox')}
          data-testid="hero-paste-text"
          className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-white/15 backdrop-blur-sm text-white font-bold text-sm hover:bg-white/25 transition"
        >
          <Upload className="w-4 h-4" /> Or paste an email / drop a screenshot
        </button>

        <button
          type="button"
          onClick={() => navigate('/admin/finance/expenses')}
          data-testid="hero-view-all-expenses"
          className="mt-2 w-full flex items-center justify-center gap-2 py-2 text-white/90 hover:text-white text-xs font-bold uppercase tracking-wider transition"
        >
          <FileText className="w-3.5 h-3.5" /> View all expenses &amp; receipts →
        </button>
      </div>
    </section>
  );
};

const MissingReceiptsLink = ({ stats, navigate }) => {
  const missing = stats?.missing_count || 0;
  const total = stats?.expense_count || 0;
  if (total === 0) {
    return (
      <div className="mt-1.5 text-[11px] text-slate-400 font-bold uppercase tracking-wider" data-testid="missing-receipts-empty">
        No expenses logged yet
      </div>
    );
  }
  if (missing === 0) {
    return (
      <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700" data-testid="missing-receipts-clean">
        <Paperclip className="w-3 h-3" /> All {total} expenses have receipts
      </div>
    );
  }
  // Stop click bubbling so the parent KPI button doesn't also navigate
  const onClick = (e) => { e.stopPropagation(); navigate('/admin/finance/expenses?filter=without'); };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="missing-receipts-link"
      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 hover:text-amber-900 underline decoration-dotted underline-offset-2"
    >
      <Paperclip className="w-3 h-3" /> {missing} missing receipt{missing === 1 ? '' : 's'}
    </button>
  );
};

const YoyBadge = ({ fy }) => {
  const pct = fy?.yoy_net_pct;
  const prevYear = fy?.prev_year;
  const prevTxns = fy?.prev_txn_count || 0;
  if (pct === null || pct === undefined) {
    return (
      <div className="mt-2 text-[11px] text-slate-400 font-bold uppercase tracking-wider" data-testid="yoy-badge-empty">
        {prevTxns === 0 ? `No ${prevYear} data yet` : `vs ${prevYear} · n/a`}
      </div>
    );
  }
  const positive = pct > 0;
  const flat = pct === 0;
  const Icon = flat ? Minus : positive ? ArrowUpRight : ArrowDownRight;
  const tone = flat
    ? 'bg-slate-100 text-slate-600 border-slate-200'
    : positive
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : 'bg-rose-100 text-rose-700 border-rose-200';
  const sign = positive ? '+' : '';
  return (
    <div className="mt-2 inline-flex items-center gap-1" data-testid="yoy-badge">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tone} text-[11px] font-bold`}>
        <Icon className="w-3 h-3" />
        {sign}{pct}%
      </span>
      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">vs {prevYear}</span>
    </div>
  );
};
